import { AnimatePresence, motion } from "framer-motion"
import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef } from "react"
import { HEIGHT_DURATION, HEIGHT_EASE, TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

/**
 * Высота блока тянется к высоте нового содержимого, когда меняется `key`, поэтому то, что
 * ниже, не прыгает. Высота задаётся только на время перехода, потом снова `auto`: иначе
 * любое изменение внутри (раскрылся список, догрузилась картинка) отставало бы от
 * содержимого. На время перехода края обрезаются, чтобы уходящий блок не налезал на соседей.
 */
export function useHeightFollow(ref: RefObject<HTMLElement | null>, key: string, enabled: boolean) {
	const settled = useRef(0)
	const animating = useRef(false)
	const previous = useRef(key)

	useEffect(() => {
		const element = ref.current
		if (!element || !enabled) return
		settled.current = element.offsetHeight
		const observer = new ResizeObserver(() => {
			if (!animating.current) settled.current = element.offsetHeight
		})
		observer.observe(element)
		return () => observer.disconnect()
	}, [ref, enabled])

	useLayoutEffect(() => {
		const element = ref.current
		if (!element || !enabled || previous.current === key) return
		previous.current = key
		// Прерванный переход начинаем с той высоты, что видна сейчас, а не с конечной.
		const from = animating.current ? element.getBoundingClientRect().height : settled.current
		element.style.transition = "none"
		element.style.height = ""
		const to = element.offsetHeight
		if (Math.abs(from - to) < 1) {
			animating.current = false
			element.style.overflow = ""
			return
		}
		animating.current = true
		element.style.height = `${from}px`
		element.style.overflow = "clip"
		void element.offsetHeight
		element.style.transition = `height ${HEIGHT_DURATION}ms ${HEIGHT_EASE}`
		element.style.height = `${to}px`
		const finish = (event?: TransitionEvent) => {
			if (event && (event.target !== element || event.propertyName !== "height")) return
			element.removeEventListener("transitionend", finish)
			animating.current = false
			element.style.transition = ""
			element.style.height = ""
			element.style.overflow = ""
			settled.current = element.offsetHeight
		}
		element.addEventListener("transitionend", finish)
		// Страховка: transitionend не приходит, если вкладка была скрыта.
		const timer = window.setTimeout(finish, HEIGHT_DURATION + 120)
		return () => {
			window.clearTimeout(timer)
			element.removeEventListener("transitionend", finish)
		}
	}, [ref, key, enabled])
}

/**
 * Смена состояний блока без рывка: скелетон и содержимое, список и другой список, «пусто»
 * и ошибка. Уходящее вынимается из потока и гаснет поверх, не принимая клики, новое
 * проявляется на своём месте, поэтому высота не складывается из двух блоков ни на кадр.
 * Если данные уже в кэше, содержимое показывается сразу.
 *
 * Без `id` различаются только загрузка и готовность. С `id` переход идёт на каждую его
 * смену: передавайте ключ состояния, которое реально на экране («error», «empty»,
 * `list-${shownKey}`), а не ключ фильтра. Высота блока тянется к высоте нового состояния,
 * поэтому соседи снизу не прыгают; `autoHeight={false}` только там, где высоту задаёт
 * родитель.
 */
export function LoadSwap({
	loading,
	id,
	children,
	className,
	fill = false,
	autoHeight = true,
}: {
	loading: boolean
	id?: string
	children: ReactNode
	className?: string
	/** Растянуть на высоту родителя: для экранов вроде редактора воронки. */
	fill?: boolean
	autoHeight?: boolean
}) {
	const key = loading ? "loading" : (id ?? "ready")
	const container = useRef<HTMLDivElement>(null)
	useHeightFollow(container, key, autoHeight && !fill)
	// Содержимое после скелетона чуть приподнимается, смена готовых состояний идёт на месте:
	// сдвиг на каждом переключении фильтра читался бы как дрожь.
	const shown = useRef(key)
	const fromSkeleton = shown.current === "loading"
	useEffect(() => {
		shown.current = key
	}, [key])

	return (
		<div ref={container} className={cn("relative", fill && "h-full", className)}>
			<AnimatePresence initial={false} mode="popLayout">
				<motion.div
					key={key}
					initial={{ opacity: 0, y: fromSkeleton ? 4 : 0 }}
					animate={{
						opacity: 1,
						y: 0,
						transition: fromSkeleton ? TRANSITION.crossfade : TRANSITION.swapIn,
					}}
					exit={{ opacity: 0, pointerEvents: "none", transition: TRANSITION.swapOut }}
					className={fill ? "h-full" : undefined}
				>
					{children}
				</motion.div>
			</AnimatePresence>
		</div>
	)
}
